import {Density} from "@/enum/density";
import {keywordDensityStatus, keywordDensityThresholds} from "@/utils/ArticleComputed";
import {stripTagsAndEntities} from "@/utils/string";
import {anyKeywordCovered} from "@/service/keywordStemDensity";
import {SeoScoreMetric, SeoScoreMetricName} from "@/model/SeoScore";
import {scoreStatusThreshold} from "@/service/geoEatScore";
import {EventBus} from "@/utils/EventBus";
import {requestKeywordAnalysisOpen} from "@/service/keywordAnalysisNavigate";

export function richContentIncludesPermutation(content: string, searchList: Array<string>, languageCode?: string | null): boolean {
    const strippedContent = stripTagsAndEntities(content);
    return anyKeywordCovered(strippedContent, searchList, languageCode);
}

const expandKeywordsDensity = () => {
    EventBus.$emit('workspace-drawer-open');
    EventBus.$emit('keywords-expand');
};

const expandKeywordAnalysis = () => {
    requestKeywordAnalysisOpen();
};

const expandH1Inspiration = () => {
    EventBus.$emit('h1-inspiration-expand');
};

const openSchemaPreview = () => {
    EventBus.$emit('article-preview-schema');
};

/**
 * @src https://docs.google.com/spreadsheets/d/1OqESKAo-okjUFg916kevKUlAl67lA8ih4TZnAbcl_gw/edit#gid=0
 */
export const seoScoreMetrics: Record<SeoScoreMetricName, SeoScoreMetric> = {
    aiVisibility: {
        getStatus: function (value) {
            return scoreStatusThreshold(value as number);
        },
        getTrParams: (value) => [value],
        canAutoImprove: false,
        domSelector: '#answers',
    },
    eeatScore: {
        getStatus: function (value) {
            return scoreStatusThreshold(value as number);
        },
        getTrParams: (value) => [value],
        canAutoImprove: false,
        domSelector: '#eatAttribution',
    },
    eatAuthor: {
        getStatus: function (value) {
            return value ? 'GREEN' : 'RED';
        },
        canAutoImprove: false,
        domSelector: '#eatAttribution',
    },
    eatDate: {
        getStatus: function (value) {
            return value ? 'GREEN' : 'RED';
        },
        canAutoImprove: false,
        domSelector: '#eatAttribution',
    },
    eatBio: {
        getStatus: function (value) {
            return value ? 'GREEN' : 'YELLOW';
        },
        canAutoImprove: false,
        domSelector: '#eatAttribution',
    },
    schemaMarkup: {
        getStatus: function (value) {
            return value ? 'GREEN' : 'RED';
        },
        canAutoImprove: true,
        getClickAction: openSchemaPreview,
    },
    faqPresent: {
        getStatus: function (value) {
            return (value as number) >= 1 ? 'GREEN' : 'RED';
        },
        canAutoImprove: false,
        getTrParams: (value) => [value],
        domSelector: '#answers',
    },
    keywordVariants: {
        getStatus: function (value) {
            value = value as number;
            return value >= 2 ? 'GREEN' : value > 0 ? 'YELLOW' : 'RED';
        },
        canAutoImprove: false,
        getClickAction: expandKeywordAnalysis,
        domSelector: '#keywordSelectionContainer',
    },
    keywordInSlug: {
        getStatus: function (value) {
            return value ? 'GREEN' : 'RED';
        },
        canAutoImprove: true,
        domSelector: '#slug'
    },
    imgAltIncludesKeyword: {
        getStatus: function (value) {
            return value ? 'GREEN' : 'RED';
        },
        canAutoImprove: true,
        domSelector: '#images'
    },
    titleIncludesKeyword: {
        getStatus: function (value, referenceValue) {
            return richContentIncludesPermutation(value as string, referenceValue as Array<string>) ? 'GREEN' : 'RED';
        },
        canAutoImprove: false,
        domSelector: '#metaTitle'
    },
    titleLength: {
        getStatus: function (value, referenceValue) {
            return (value as number) <= (referenceValue as number) ? 'GREEN' : 'RED';
        },
        canAutoImprove: false,
        domSelector: '#metaTitle'
    },
    descriptionIncludesKeyword: {
        getStatus: function (value, referenceValue) {
            return richContentIncludesPermutation(value as string, referenceValue as Array<string>) ? 'GREEN' : 'RED';
        },
        canAutoImprove: false,
        domSelector: '#metaDescription'
    },
    descriptionLength: {
        getStatus: function (value, referenceValue) {
            return (value as number) <= (referenceValue as number) ? 'GREEN' : 'RED';
        },
        canAutoImprove: false,
        domSelector: '#metaDescription'
    },
    keywordDensity: {
        getStatus: function (density) {
            const densityStatus = keywordDensityStatus(density as number);
            return densityStatus === Density.OK ? 'GREEN' : densityStatus === Density.LOW ? 'YELLOW' : 'RED';
        },
        getTrParams: (density) => [
            Math.round(density as number * 100) / 100,
            keywordDensityThresholds[Density.LOW],
            keywordDensityThresholds[Density.HIGH]
        ],
        canAutoImprove: false,
        getClickAction: expandKeywordsDensity,
        domSelector: '#articleKeywords',
    },
    serpIsComplete: {
        getStatus: function (value) {
            return value ? 'GREEN' : 'RED';
        },
        canAutoImprove: true,
        domSelector: '#serpSnippet'
    },
    avgWordCountOfTopTen: {
        getStatus: function (value, referenceValue) {
            return (value as number) >= (referenceValue as number) ? 'GREEN' : 'RED';
        },
        canAutoImprove: false,
        domSelector: '#h1',
    },
    longestWordCountOfTopTen: {
        getStatus: function (value, referenceValue) {
            return (value as number) >= (referenceValue as number) ? 'GREEN' : 'YELLOW';
        },
        canAutoImprove: false,
        domSelector: '#h1',
    },
    keywordInIntroduction: {
        getStatus: function (value, referenceValue, languageCode?: string) {
            return richContentIncludesPermutation(
                value as string,
                referenceValue as Array<string>,
                languageCode
            ) ? 'GREEN' : 'RED';
        },
        canAutoImprove: false,
        domSelector: '#h1pw'
    },
    keywordInH1: {
        getStatus: function (value, referenceValue) {
            value = value as Array<string>;
            return value.some((h1h: string) =>
                richContentIncludesPermutation(h1h, referenceValue as Array<string>)
            ) ? 'GREEN' : 'RED';
        },
        canAutoImprove: false,
        domSelector: '#h1'
    },
    keywordInH2: {
        getStatus: function (value, referenceValue) {
            value = value as Array<string>;
            return value.some((h2h: string) =>
                richContentIncludesPermutation(h2h, referenceValue as Array<string>)
            ) ? 'GREEN' : 'RED';
        },
        canAutoImprove: false,
        domSelector: '#h2'
    },
    keywordInH3: {
        getStatus: function (value, referenceValue) {
            value = value as Array<string>;
            return value.some((h3h: string) =>
                richContentIncludesPermutation(h3h, referenceValue as Array<string>)
            ) ? 'GREEN' : 'RED';
        },
        canAutoImprove: false,
        domSelector: '#h3'
    },
    relatedKeywords: {
        getStatus: function (value) {
            return (value as number) > 0.5 ? 'GREEN' : 'RED';
        },
        getTrParams: function (value) {
            return [Math.round(value as number * 100) + '%'];
        },
        canAutoImprove: false,
        getClickAction: expandKeywordAnalysis,
        domSelector: '#keywordSelectionContainer',
    },
    inboundLinks: {
        getStatus: function (value) {
            return (value as number) >= 1 ? 'GREEN' : 'RED';
        },
        canAutoImprove: false,
        domSelector: '#h1',
    },
    outboundLinks: {
        getStatus: function (value) {
            return (value as number) >= 1 ? 'GREEN' : 'RED';
        },
        canAutoImprove: false,
        domSelector: '#h1',
    },
    inspirationFromTopTen: {
        getStatus: function (value) {
            return value ? 'GREEN' : 'RED';
        },
        canAutoImprove: false,
        getClickAction: expandH1Inspiration,
        domSelector: '#h1',
    },
    subHeadingDistribution: {
        getStatus: function (value, referenceValue) {
            return value === referenceValue ? 'GREEN' : 'RED';
        },
        canAutoImprove: true,
        domSelector: '#h2',
    },
    paragraphLength: {
        getStatus: function (shortParagraphRatio) {
            shortParagraphRatio = shortParagraphRatio as number;
            return shortParagraphRatio > 0.99 ? 'GREEN' : shortParagraphRatio > 0.5 ? 'YELLOW' : 'RED';
        },
        getTrParams: function (value) {
            return [Math.round(value as number * 100) + '%'];
        },
        canAutoImprove: false,
        domSelector: '#h1',
    },
    sentenceLength: {
        getStatus: function (avgSentenceWordCount) {
            avgSentenceWordCount = avgSentenceWordCount as number;
            return avgSentenceWordCount < 20 ? 'GREEN' : avgSentenceWordCount < 25 ? 'YELLOW' : 'RED';
        },
        getTrParams: function (value) {
            return [value, 20, 25];
        },
        canAutoImprove: false,
        domSelector: '#h1',
    }
};
